/**
 * The tidying the platform's own sentences get, for the ones the web builds
 * itself.
 *
 * The mission's texts are templates with `%PLACE%`, `%PLACE2%` and `%CAST%` in
 * them, and the platform runs every filled one through `Narrative.tidy` before
 * it reaches a player: "de el poble" becomes "del poble", "de Agia Marina"
 * becomes "d’Agia Marina". The quick start's twist preview filled the same
 * templates with its own stand-ins and printed the result raw, so a creator
 * read "prop de un nucli proper" and "l’objectiu ha deixat" starting in
 * lower-case on a page whose published text says neither
 * (`QUICK_START_REVIEW.md` F14).
 *
 * This is the same three rules, in the same order, so both sides of the wire
 * write the same Catalan. The Java is
 * `platform/src/main/java/ops/platform/generator/Narrative.java`; keep them
 * together.
 */

/**
 * A preposition on its own, before "el" or "els". The lookbehind is the whole
 * point: it tells the "a" of "a el poble" from the last letter of "Assalta el
 * poble".
 */
const JOINS = /(?<![\p{L}’'])(de|De|a|A) (els?) /gu;

/**
 * "de" before a vowel or an h is written "d’": "el radar d’Agia Marina", never
 * "de Agia Marina". Applied after the joins above, so "de el" has already
 * become "del" and is not touched. Only "de" elides: "a Agia Marina" is
 * correct Catalan and stays.
 */
const ELISION = /\b(d|D)e ([aeiouàèéíòóúAEIOUÀÈÉÍÒÓÚhH])/gu;

/** "de"+"el" is "del", "a"+"els" is "als", and a capital preposition keeps its capital. */
function join(_match: string, preposition: string, article: string): string {
  const of = preposition.length === 2;
  const plural = article.length === 3;
  const joined = of ? (plural ? 'dels' : 'del') : plural ? 'als' : 'al';
  const cased =
    preposition[0] === preposition[0].toUpperCase()
      ? joined[0].toUpperCase() + joined.slice(1)
      : joined;
  return `${cased} `;
}

/**
 * A sentence that starts with a filled placeholder starts with whatever the
 * placeholder was — "l’objectiu", a person's name — and a sentence starts with
 * a capital.
 */
export function capitalise(text: string): string {
  return text.length === 0 ? text : text[0].toLocaleUpperCase('ca') + text.slice(1);
}

/** The prepositions joined, the elisions made, and a capital at the start. */
export function tidy(text: string): string {
  return capitalise(text.replace(JOINS, join).replace('  ', ' ').replace(ELISION, '$1’$2'));
}
